import Scrollbar from "src/components/Scrollbar";
import useAuth from "src/hooks/useAuth";
import MenuPopover from "../../../components/MenuPopover";
import { useEffect, useState } from "react";
import {
    Box,
    Divider,
    Typography,
    ListItemButton,
    ListItemIcon,
    List,
    IconButton,
    ListItem,
    ListItemButtonProps,
} from "@mui/material";
import { IconButtonAnimate } from "../../../components/animate";
import {
    AvailableIcon,
    UnavailableIcon,
    LoginIcon,
    MoreVerticalIcon,
    UserIndividualIcon,
    UserBusinessIcon,
    UserCSOIcon,
} from "src/theme/overrides/CustomIcons";
import { ListItemText } from "@mui/material";
import LoadingButton from "src/components/LoadingButton";
import { ListSubheader } from "@mui/material";
import { MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useCompanyList } from "src/modules/company/hooks/useCompanyList";
import { useUserInvitationList } from "src/modules/user/hooks/UserInvitation/useUserInvitationList";
import { PATH_ACCOUNT_BILLING, PATH_CHANGE_ORGANISATION, PATH_MY_ACCOUNT } from "src/routes/paths";
import { useNavigate } from "react-router";
import { useUserInvitationAcceptMutation } from "src/modules/user/hooks/UserInvitation/useUserInvitationAcceptMutation";
import { useUserInvitationRejectMutation } from "src/modules/user/hooks/UserInvitation/useUserInvitationRejectMutation";
import { CompanyListModel, CompanyType } from "src/modules/company/types/Company";
import { useProfileUserPermissions } from "src/modules/profile/hooks/Profile/useUserPermissions";
import { UserRoles } from "src/modules/user/types/UserRoles";

interface AccountItemProps extends ListItemButtonProps {
    id: string;
    name: string;
    status: "active" | "inactive";
    handleAction?: (actionType: "accept" | "reject") => void;
    type: CompanyType;
    showFinishRegistration?: boolean;
}

const AccountItem = ({
    name,
    status = "inactive",
    handleAction,
    type,
    showFinishRegistration = false,
    ...props
}: AccountItemProps) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<HTMLElement | null>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault;
        setOpen(event.currentTarget);
    };

    const handleClose = () => {
        setOpen(null);
    };

    return (
        <>
            <ListItem
                secondaryAction={
                    status === "inactive" && (
                        <IconButton size="small" onClick={handleOpen}>
                            <MoreVerticalIcon sx={{ width: 20, height: 20 }} />
                        </IconButton>
                    )
                }
                sx={{
                    p: 0,
                    "&.Mui-selected": {
                        color: "primary.main",
                    },
                }}
            >
                <ListItemButton
                    sx={{
                        m: 0,
                        py: 1.5,
                        "&.Mui-selected": {
                            color: "primary.main",
                            backgroundColor: "transparent",
                        },
                        "&.Mui-selected:hover": {
                            backgroundColor: "action.hover",
                        },
                    }}
                    {...props}
                >
                    <ListItemIcon
                        sx={{
                            alignSelf: "flex-start",
                            pt: 0.1,
                            color: "grey.600",
                            ".Mui-selected &": {
                                color: "primary.main",
                            },
                        }}
                    >
                        {type === CompanyType.HOME && (
                            <UserIndividualIcon fontSize="small" color="inherit" />
                        )}
                        {type === CompanyType.BUSINESS && (
                            <UserBusinessIcon fontSize="small" color="inherit" />
                        )}
                        {type === CompanyType.PUBLIC && (
                            <UserCSOIcon fontSize="small" color="inherit" />
                        )}
                    </ListItemIcon>
                    <ListItemText
                        primary={name}
                        primaryTypographyProps={{
                            fontWeight: "medium",
                            variant: "body2",
                        }}
                        secondary={showFinishRegistration ? t("auth.login.finishReg") : undefined}
                        secondaryTypographyProps={{
                            variant: "caption",
                            color: "warning.main",
                        }}
                    />
                </ListItemButton>
            </ListItem>
            {status === "inactive" && (
                <MenuPopover
                    open={Boolean(open)}
                    anchorEl={open}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    arrow="right-top"
                    onClose={handleClose}
                    sx={{
                        width: 200,
                        px: 0,
                        mt: -1,
                        "& .MuiMenuItem-root": {
                            typography: "body2",
                            borderRadius: 0.75,
                        },
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            if (handleAction) handleAction("accept");
                            handleClose();
                        }}
                        sx={{ borderRadius: 0 }}
                    >
                        <AvailableIcon
                            sx={{
                                mr: 2,
                            }}
                            fontSize="small"
                        />
                        {t("shared.accept")}
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            if (handleAction) handleAction("reject");
                            handleClose();
                        }}
                        sx={{ borderRadius: 0 }}
                    >
                        <UnavailableIcon
                            sx={{
                                mr: 2,
                            }}
                            fontSize="small"
                        />
                        {t("shared.reject")}
                    </MenuItem>
                </MenuPopover>
            )}
        </>
    );
};

interface CompanyAccountItemProps {
    company: CompanyListModel;
    currentCompanyId?: string;
}

const CompanyAccountItem = ({ company, currentCompanyId }: CompanyAccountItemProps) => {
    const navigate = useNavigate();
    const isSubscriptionInactive = company.isSubscriptionActive !== true;
    const { data: permissions, refetch, isFetched } = useProfileUserPermissions(company.id);

    useEffect(() => {
        if (isSubscriptionInactive) {
            refetch();
        }
    }, [isSubscriptionInactive, company.id, refetch]);

    const canManageInvoices = permissions?.roles?.includes(UserRoles.ROLE_CAN_MANAGE_INVOICES);
    const canAccessInactiveCompany = !!permissions?.hasFullAccess || !!canManageInvoices;

    if (isSubscriptionInactive && !isFetched) {
        return null;
    }

    if (isSubscriptionInactive && !canAccessInactiveCompany) {
        return null;
    }

    const redirectTo = isSubscriptionInactive ? `${PATH_ACCOUNT_BILLING}?tab=0` : undefined;
    const changeOrganisationPath = PATH_CHANGE_ORGANISATION.replace(":id", company.id);

    return (
        <AccountItem
            key={company.id}
            onClick={
                currentCompanyId === company.id
                    ? undefined
                    : () =>
                          navigate(
                              redirectTo
                                  ? `${changeOrganisationPath}?redirectTo=${encodeURIComponent(
                                        redirectTo
                                    )}`
                                  : changeOrganisationPath
                          )
            }
            selected={currentCompanyId === company.id}
            name={company.name}
            id={company.id}
            type={company.type}
            status={"active"}
            showFinishRegistration={isSubscriptionInactive}
        />
    );
};

const AccountPopover = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [open, setOpen] = useState<HTMLElement | null>(null);
    const { userState } = useAuth();
    const currentCompanyId = userState?.company?.id;

    const { data: companies, refetch: refetchCompanies } = useCompanyList();
    const { data: invitations, refetch: refetchInvitations } = useUserInvitationList();

    const refetchAll = () => {
        refetchCompanies();
        refetchInvitations();
    };

    const { mutate: acceptInvitation } = useUserInvitationAcceptMutation({
        onSuccess: () => {
            refetchAll();
        },
    });

    const { mutate: rejectInvitation } = useUserInvitationRejectMutation({
        onSuccess: () => {
            refetchAll();
        },
    });

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setOpen(event.currentTarget);
    };

    const handleClose = () => {
        setOpen(null);
    };

    useEffect(() => {
        refetchAll();
    }, []);

    return (
        <>
            <IconButtonAnimate
                onClick={handleOpen}
                color={open ? "primary" : "default"}
                sx={{ width: 40, height: 40 }}
            >
                <LoginIcon sx={{ width: 20, height: 20 }} />
            </IconButtonAnimate>

            <MenuPopover
                open={Boolean(open)}
                anchorEl={open}
                onClose={handleClose}
                sx={{
                    width: 300,
                    p: 0,
                    mt: 1.5,
                    ml: 0.75,
                    "& .MuiMenuItem-root": {
                        typography: "body2",
                        borderRadius: 0.75,
                    },
                }}
            >
                <Box sx={{ my: 1.5, px: 2.5 }}>
                    <Typography variant="subtitle1" noWrap>
                        {t("shared.layout.company.title")}
                    </Typography>
                </Box>
                <Divider sx={{ borderStyle: "solid" }} />
                {companies && companies.length > 0 ? (
                    <>
                        <Scrollbar sx={{ height: { xs: 340, sm: "auto" } }}>
                            <List>
                                {(companies || []).map((company) => {
                                    return (
                                        <CompanyAccountItem
                                            key={company.id}
                                            company={company}
                                            currentCompanyId={currentCompanyId}
                                        />
                                    );
                                })}
                            </List>
                        </Scrollbar>
                        <Divider sx={{ borderStyle: "solid" }} />
                    </>
                ) : (
                    <></>
                )}
                {invitations && invitations.length > 0 ? (
                    <>
                        <Scrollbar sx={{ height: { xs: 340, sm: "auto" } }}>
                            <List
                                subheader={
                                    <ListSubheader
                                        disableSticky
                                        sx={{ py: 1, px: 2.5, typography: "overline" }}
                                    >
                                        {t("shared.layout.company.subtitle")}
                                    </ListSubheader>
                                }
                            >
                                {invitations.map((invitation) => (
                                    <AccountItem
                                        handleAction={(action) => {
                                            if (action === "accept") {
                                                acceptInvitation({
                                                    invitationId: invitation.id,
                                                });
                                            }
                                            if (action === "reject") {
                                                rejectInvitation({
                                                    invitationId: invitation.id,
                                                });
                                            }
                                        }}
                                        key={invitation.id}
                                        name={invitation?.company?.name || ""}
                                        id={`${invitation.id}`}
                                        status="inactive"
                                        type={invitation.companyType}
                                    />
                                ))}
                            </List>
                        </Scrollbar>
                        <Divider sx={{ borderStyle: "solid" }} />
                    </>
                ) : (
                    <></>
                )}
                <Box sx={{ p: 2.5 }}>
                    <LoadingButton
                        fullWidth
                        variant="outlined"
                        color="inherit"
                        onClick={() => {
                            navigate(PATH_MY_ACCOUNT);
                            handleClose();
                        }}
                    >
                        {t("shared.layout.company.button")}
                    </LoadingButton>
                </Box>
            </MenuPopover>
        </>
    );
};

export default AccountPopover;
